import { type MeResponse, hasGmsAccess } from "@seagames/contracts";
import { BrandSplash, PageTitleProvider, brandSplashArmed, disarmBrandSplash } from "@seagames/ui";
import { Outlet, createFileRoute, redirect, useLocation, useMatches, useRouter } from "@tanstack/react-router";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import splashTaglineUrl from "../assets/brand/wsl-gms-tagline.webp";
import splashWordUrl from "../assets/brand/wsl-gms-word.webp";
import { NoAccess } from "../components/auth/no-access";
import { ParticipantShell } from "../components/participant/participant-shell";
import { StageLocked } from "../components/common/stage-locked";
import { pageTitleSink } from "../components/shell/page-bar";
import { TopBar } from "../components/shell/top-bar";
import { DRAWER_ID, Sidebar } from "../components/sidebar/sidebar";
import { queryClient } from "../lib/api/query-client";
import { facilityRoleRedirect, gameSettingsRedirect, participantRedirect, sessionLostOn } from "../lib/auth/guards";
import { meQueryOptions } from "../lib/auth/session";
import { hasStoredSession } from "../lib/auth/token-store";
import { nocHomePath, nocLatestPath, stageLockFor } from "../lib/noc-stages/access";
import { clearStageLocked, useApiStageLock } from "../lib/noc-stages/locked-store";
import { useIsDocked, useSidebarCollapsed } from "../lib/ui/sidebar-collapsed";
import type { ContentTop, ContentWidth } from "../router";

/**
 * The authenticated shell. Everything under it has a session.
 *
 * The guard runs in `beforeLoad`, so a protected page never renders — not even
 * for a frame — before the server has confirmed who is asking. Rendering first
 * and redirecting in an effect is how WSL flashed a populated dashboard at
 * people who had just been signed out.
 */
export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ context, location }) => {
		// No refresh token at all: nothing to verify, don't spend a round trip.
		if (!hasStoredSession()) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}

		let session: MeResponse;
		try {
			session = await context.queryClient.ensureQueryData(meQueryOptions);
		} catch (err) {
			// Only a ruling on the credential is a sign-out. Anything else — a 503,
			// but also a 500 (the DB dropped mid-refresh, 2026-10-29) — is
			// rethrown so the error boundary offers "try again" with the session
			// intact; bouncing to /login here would be the WSL mass logout, just
			// relocated into the router.
			if (!sessionLostOn(err)) throw err;
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}

		// Server-enforced, mirrored here only so the user lands somewhere useful.
		// The API answers 403 PASSWORD_CHANGE_REQUIRED on every other route
		// regardless of what this component decides.
		if (session.must_change_password) {
			throw redirect({ to: "/change-password" });
		}

		/*
		 * Signed in is not the same as allowed in. A role without GMS access
		 * (WORKFORCE, PUBLIC) or a scoped role without an active user group of
		 * that role gets the "no access" page INSTEAD of the shell — rendered, not redirected: it is
		 * not a sign-out, the tokens stay, and switching to a role that works
		 * re-runs this guard (router.invalidate) and lets them in. The API
		 * enforces the same rules on every request; this only says so up front.
		 */
		// A participant has its own pages (/me…), and only those; staff never land on them.
		participantRedirect(session, location.pathname);
		// A facility login (transport provider) has one module; anywhere else sends it there.
		facilityRoleRedirect(session, location.pathname);
		gameSettingsRedirect(session, location.pathname);
		const gmsAccess = hasGmsAccess(session);
		/*
		 * M7c NOC stages (ORGANIZATION only; null for everyone else). A NOC whose
		 * dashboard is not open yet starts on its first open page (EOI at stage 1)
		 * — after sign-in and on "/". Any other locked page is NOT redirected: the
		 * shell draws the "not open yet" screen in its place (AppShell).
		 */
		if (gmsAccess && location.pathname === "/" && stageLockFor(session, "/")) {
			const home = nocHomePath(session);
			if (home && home !== "/") throw redirect({ to: home, replace: true });
		}
		return { session, gmsAccess };
	},
	component: AppLayout,
});

function AppLayout() {
	const { session, gmsAccess } = Route.useRouteContext();
	// No <Outlet/> for a session without access, so no page below mounts or fetches.
	if (!gmsAccess) return <NoAccess session={session} />;
	// A participant never sees the staff shell, sidebar or sectors (user, 2026-11-03).
	if (session.active_role === "PARTICIPANT") return <ParticipantShell session={session} />;
	return <AppShell session={session} />;
}

function AppShell({ session }: { session: MeResponse }) {
	const { t } = useTranslation("nav");
	const pathname = useLocation({ select: (l) => l.pathname });
	const router = useRouter();
	/*
	 * M7c: a page the NOC's stage has not opened renders the locked screen
	 * INSTEAD of <Outlet/>, so the page never mounts and fires no request.
	 * Two sources: `/auth/me.noc_stage` (the guard's view), and a 403
	 * STAGE_LOCKED the API answered on this page (the stage moved back while
	 * it was open). The latter re-reads `/auth/me` once, so the sidebar's
	 * locks and the guard catch up; it is never a sign-out.
	 */
	const meLock = stageLockFor(session, pathname);
	const apiLock = useApiStageLock(pathname);
	const lock = meLock ?? apiLock;
	const apiLockSeq = apiLock?.seq;
	useEffect(() => {
		if (apiLockSeq === undefined) return;
		void queryClient
			.fetchQuery({ ...meQueryOptions, staleTime: 0 })
			.then(() => router.invalidate())
			.catch(() => undefined);
	}, [apiLockSeq, router]);
	// A refusal belongs to the page it was answered on: leaving that page drops it.
	const shownPath = useRef(pathname);
	useEffect(() => {
		if (shownPath.current === pathname) return;
		shownPath.current = pathname;
		clearStageLocked();
	}, [pathname]);
	// Right after sign-in only (the login page arms it): the WSL GMS lockup, folding onto the sidebar.
	const [splash, setSplash] = useState(brandSplashArmed);
	useEffect(disarmBrandSplash, []);
	const wantsCollapsed = useSidebarCollapsed();
	const docked = useIsDocked();
	const collapsed = wantsCollapsed && docked;
	// The deepest route that names a wrapper wins (router.tsx: ContentWidth / ContentTop).
	const contentWidth = useMatches({
		select: (matches) => matches.findLast((m) => m.staticData.contentWidth)?.staticData.contentWidth ?? "page",
	});
	const contentTop = useMatches({
		select: (matches) => matches.findLast((m) => m.staticData.contentTop)?.staticData.contentTop ?? "page",
	});

	/*
	 * lg+: the sidebar, fixed on the left, content beside it.
	 * Below lg: daisyUI drawer — same sidebar, off-canvas behind a hamburger.
	 * The design targets 1080p desks; the drawer is the graceful path for a
	 * tablet at a venue, not a second design.
	 *
	 * lg+ only: the sidebar can be minimised to an icon rail. The shell owns
	 * the width (`--sidebar-w` is swapped here, so everything sized from it
	 * follows); the "<" that flips it lives in the sidebar's own header.
	 *
	 * The top bar (components/shell/top-bar.tsx) carries the page's title,
	 * subtitle, back arrow and actions — pages set them with `usePageBar`
	 * (components/shell/page-bar.tsx) and draw no in-page <h1>. `<ListHeader>`
	 * titles reach it through the PageTitleProvider.
	 *
	 * Content column: her page wrappers. Gutters 32px from lg, 24px from md,
	 * 16px on phones; the width cap and the space above come from the route's
	 * `staticData.contentWidth` / `contentTop` (router.tsx), default her `.pg`:
	 * 1240px including the gutters, centred, 28px down.
	 */
	return (
		<PageTitleProvider sink={pageTitleSink}>
			<div
				className="drawer min-h-dvh bg-base-200 lg:drawer-open"
				style={collapsed ? ({ "--sidebar-w": "var(--sidebar-w-collapsed)" } as CSSProperties) : undefined}
			>
				<input id={DRAWER_ID} type="checkbox" className="drawer-toggle" />

				<div className="drawer-content flex min-h-dvh min-w-0 flex-col">
					<TopBar />

					<main
						className={`mx-auto w-full flex-1 px-[1rem] pt-[1rem] pb-[1.5rem] md:px-[1.5rem] md:pb-[2.5rem] lg:px-[2rem] lg:pb-[4.5rem] ${CONTENT_WIDTH[contentWidth]} ${CONTENT_TOP[contentTop]}`}
					>
						{lock ? <StageLocked lock={lock} pathname={pathname} home={nocLatestPath(session)} /> : <Outlet />}
					</main>
				</div>

				<div className="drawer-side z-20">
					<label htmlFor={DRAWER_ID} aria-label={t("shell.closeMenu")} className="drawer-overlay" />
					<Sidebar session={session} collapsed={collapsed} />
				</div>
			</div>
			{splash && (
				<BrandSplash tone="gms" wordSrc={splashWordUrl} taglineSrc={splashTaglineUrl} onDone={() => setSplash(false)} />
			)}
		</PageTitleProvider>
	);
}

/** Her wrappers' caps, including the gutters (router.tsx `ContentWidth`). */
const CONTENT_WIDTH: Record<ContentWidth, string> = {
	page: "max-w-[77.5rem]", // .pg — 1240px
	full: "", // .pg--dash
};

/** Her space above the content from tablet up (router.tsx `ContentTop`); phones are 16px on every page. */
const CONTENT_TOP: Record<ContentTop, string> = {
	page: "md:pt-[1.5rem] lg:pt-[1.75rem]", // .pg — 24 / 28px
	tabbed: "md:pt-[1.25rem]", // .pg--sl — 20px
};
